<template>
  <div class="importacao-container">
    <!-- Header da página -->
    <div class="page-header">
      <div class="header-left">
        <div class="header-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        </div>
        <div class="header-text">
          <h1>Importação de Dados SIGAA</h1>
          <p>
            Selecione os arquivos CSV dos relatórios para atualizar e consolidar
            os dados no GEX
          </p>
        </div>
      </div>

      <!-- Resumo rápido de progresso -->
      <div class="header-pills">
        <div class="status-pill" :class="{
          'pill-complete': totalObrigatoriosConcluidos === totalObrigatorios,
        }">
          <span class="pill-dot"></span>
          <span class="pill-text">
            Obrigatórios:
            <strong>{{ totalObrigatoriosConcluidos }}/{{ totalObrigatorios }}</strong>
          </span>
        </div>
        <div class="status-pill pill-secondary">
          <span class="pill-dot opcional"></span>
          <span class="pill-text">
            Orçamento:
            <strong>{{
              relatorioOrcamentoConcluido
                ? "Incluído"
                : "Não incluído (Opcional)"
            }}</strong>
          </span>
        </div>
      </div>
    </div>

    <!-- Lista de relatórios (Cards Retangulares Horizontais) -->
    <div class="relatorios-lista">
      <div v-for="relatorio in relatorios" :key="relatorio.id" class="relatorio-card" :class="{
        'card-importing': relatorio.importando,
        'card-done': relatorio.concluido,
        'card-dragover': relatorio.isDragging,
        'card-optional': !relatorio.obrigatorio,
      }" @dragover.prevent="relatorio.isDragging = true" @dragenter.prevent="relatorio.isDragging = true"
        @dragleave.prevent="relatorio.isDragging = false" @drop.prevent="onDrop($event, relatorio)">
        <!-- Conteúdo principal do card (Esquerda) -->
        <div class="card-info">
          <div class="card-icon" :style="{ background: relatorio.gradiente }">
            <span v-html="relatorio.icone"></span>
          </div>
          <div class="card-details">
            <div class="card-title-row">
              <h3 class="card-title">{{ relatorio.titulo }}</h3>
              <span class="badge-tipo" :class="relatorio.obrigatorio ? 'badge-obrigatorio' : 'badge-opcional'
                ">
                {{ relatorio.obrigatorio ? "Obrigatório" : "Opcional" }}
              </span>
            </div>
            <p class="card-desc">{{ relatorio.descricao }}</p>

            <!-- Mostra arquivo selecionado se importado com sucesso -->
            <div v-if="relatorio.concluido" class="import-success-info">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>Pronto para consolidação:
                <strong>{{ relatorio.arquivoNome }}</strong></span>
            </div>
          </div>
        </div>

        <!-- Ações do card (Direita) -->
        <div class="card-actions">
          <input type="file" :id="'fileInput-' + relatorio.id" accept=".csv" @change="onFileSelected($event, relatorio)"
            style="display: none" />

          <!-- Barra de progresso inline se importando -->
          <div v-if="relatorio.importando" class="progress-wrapper">
            <div class="progress-bar-container">
              <div class="progress-bar-fill" :style="{ width: relatorio.progresso + '%' }"></div>
            </div>
            <span class="progress-text">Carregando {{ Math.round(relatorio.progresso) }}%</span>
          </div>

          <!-- Botões de ação quando concluído ou aguardando -->
          <div v-else class="actions-group">
            <button class="btn-importar-acao" :class="{ 'btn-concluido': relatorio.concluido }"
              @click="triggerImport(relatorio.id)">
              <!-- Ícone correspondente -->
              <svg v-if="!relatorio.concluido" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
                stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>{{
                relatorio.concluido ? "Alterar CSV" : "Importar CSV"
              }}</span>
            </button>

            <!-- Botão de remover arquivo se importado -->
            <button v-if="relatorio.concluido" class="btn-remover-arquivo" title="Remover este arquivo"
              @click.stop="removerArquivo(relatorio)">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Botão de finalização (Exportar GEX) -->
    <div v-if="todosObrigatoriosConcluidos" class="finalizacao-section">
      <div class="finalizacao-alert">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        <div class="finalizacao-texto">
          <strong>Relatórios obrigatórios validados!</strong>
          <span v-if="relatorioOrcamentoConcluido">
            Todos os 9 relatórios obrigatórios e o relatório opcional de
            <strong>Orçamento</strong> foram adicionados ao lote.
          </span>
          <span v-else>
            Todos os 9 relatórios obrigatórios foram adicionados ao lote. O
            relatório opcional de <strong>Orçamento</strong> não foi anexado e
            será ignorado na consolidação.
          </span>
        </div>
      </div>
      <button class="btn-exportar-gex" @click="exportarParaGex" :disabled="exportando">
        <span v-if="exportando" class="spinner-mini"></span>
        <span v-else>Exportar para o GEX</span>
      </button>
    </div>
  </div>
  <ModalAviso v-if="mostrarAviso" :sucesso="avisoSucesso" :texto="textoAviso" @fecharModal="fecharAviso" />
</template>

<script setup>
import { reactive, ref, computed } from "vue";
import sigaaService from "@/service/sigaaService";
import ModalAviso from "@/components/common/aviso.vue";


const relatorios = reactive([
  {
    id: "atividade",
    titulo: "Relatório de Atividades da Instituição",
    descricao: "Dados das atividades de extensão registradas na instituição",
    nomeArquivo: "CONSULTA_RELATORIO_ATIVIDADES_INSTITUICAO",
    obrigatorio: true,
    icone:
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="14" x2="8" y2="14.01"/><line x1="12" y1="14" x2="12" y2="14.01"/><line x1="16" y1="14" x2="16" y2="14.01"/><line x1="8" y1="18" x2="8" y2="18.01"/><line x1="12" y1="18" x2="12" y2="18.01"/></svg>',
    gradiente: "linear-gradient(135deg, #6f319e, #9b59b6)",
    importando: false,
    progresso: 0,
    concluido: false,
    arquivoNome: "",
    file: null,
    isDragging: false,
  },
  {
    id: "autorizacao",
    titulo: "Relatório de Autorizações de Extensão",
    descricao: "Registros de autorizações concedidas para ações de extensão",
    nomeArquivo: "CONSULTA_RELATORIO_AUTORIZACOES_EXTENSAO",
    obrigatorio: true,
    icone:
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>',
    gradiente: "linear-gradient(135deg, #3b2479, #6f319e)",
    importando: false,
    progresso: 0,
    concluido: false,
    arquivoNome: "",
    file: null,
    isDragging: false,
  },
  {
    id: "membro",
    titulo: "Relatório de Membros de Extensão",
    descricao: "Dados dos membros participantes das ações extensionistas",
    nomeArquivo: "CONSULTA_RELATORIO_MEMBROS_EXTENSAO",
    obrigatorio: true,
    icone:
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>',
    gradiente: "linear-gradient(135deg, #513187, #9b59b6)",
    importando: false,
    progresso: 0,
    concluido: false,
    arquivoNome: "",
    file: null,
    isDragging: false,
  },
  {
    id: "curricularizacao",
    titulo: "Relatório de Curricularização",
    descricao: "Dados do relatório de curricularização de extensão",
    nomeArquivo: "CONSULTA_RELATORIO_CURRICULARIZACAO",
    obrigatorio: true,
    icone:
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>',
    gradiente: "linear-gradient(135deg, #3b2479, #8e44ad)",
    importando: false,
    progresso: 0,
    concluido: false,
    arquivoNome: "",
    file: null,
    isDragging: false,
  },
  {
    id: "parceria",
    titulo: "Relatório de Parcerias Externas",
    descricao: "Dados de instituições parceiras nas ações de extensão",
    nomeArquivo: "CONSULTA_RELATORIO_PARCERIAS_EXTERNAS",
    obrigatorio: true,
    icone:
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m11 17 2 2a1 1 0 0 0 1.42 0l4.58-4.58a1 1 0 0 0 0-1.42l-3.58-3.58a1 1 0 0 0-1.42 0L12 11"/><path d="m13 7 2-2a1 1 0 0 1 1.42 0l4.58 4.58a1 1 0 0 1 0 1.42L19 13"/><path d="m3 11 4.59-4.59a1 1 0 0 1 1.41 0l3.59 3.59a1 1 0 0 1 0 1.41L8 16"/><path d="m7 15-2 2a1 1 0 0 1-1.42 0L2 15.42a1 1 0 0 1 0-1.42L5 11"/></svg>',
    gradiente: "linear-gradient(135deg, #3a2474, #48298a)",
    importando: false,
    progresso: 0,
    concluido: false,
    arquivoNome: "",
    file: null,
    isDragging: false,
  },
  {
    id: "local",
    titulo: "Relatório de Locais de Realização",
    descricao:
      "Municípios, bairros, abrangência e espaços de realização das ações",
    nomeArquivo: "CONSULTA_RELATORIO_LOCAIS_REALIZACAO",
    obrigatorio: true,
    icone:
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>',
    gradiente: "linear-gradient(135deg, #1f5080, #2980b9)",
    importando: false,
    progresso: 0,
    concluido: false,
    arquivoNome: "",
    file: null,
    isDragging: false,
  },
  {
    id: "palavraChave",
    titulo: "Relatório de Palavras-Chave e Metadados",
    descricao: "Palavras-chave temáticas, público-alvo, URLs e redes sociais",
    nomeArquivo: "CONSULTA_RELATORIO_PALAVRAS_CHAVES",
    obrigatorio: true,
    icone:
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>',
    gradiente: "linear-gradient(135deg, #7b1fa2, #e91e63)",
    importando: false,
    progresso: 0,
    concluido: false,
    arquivoNome: "",
    file: null,
    isDragging: false,
  },
  {
    id: "indicador",
    titulo: "Relatório de Indicadores de Extensão",
    descricao:
      "Indicadores, metas qualitativas/quantitativas e resultados esperados",
    nomeArquivo: "CONSULTA_RELATORIO_INDICADORES",
    obrigatorio: true,
    icone:
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>',
    gradiente: "linear-gradient(135deg, #007991, #1abc9c)",
    importando: false,
    progresso: 0,
    concluido: false,
    arquivoNome: "",
    file: null,
    isDragging: false,
  },
  {
    id: "cronograma",
    titulo: "Relatório de Cronogramas de Projeto",
    descricao:
      "Etapas, atividades do cronograma, datas de início e fim da ação",
    nomeArquivo: "CONSULTA_RELATORIO_CRONOGRAMAS",
    obrigatorio: true,
    icone:
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
    gradiente: "linear-gradient(135deg, #302b63, #24243e)",
    importando: false,
    progresso: 0,
    concluido: false,
    arquivoNome: "",
    file: null,
    isDragging: false,
  },
  {
    id: "orcamento",
    titulo: "Relatório de Orçamento e Financiamento",
    descricao:
      "Previsão orçamentária, fontes de receita, valores e elementos de despesa",
    nomeArquivo: "CONSULTA_RELATORIO_ORCAMENTO",
    obrigatorio: false,
    icone:
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>',
    gradiente: "linear-gradient(135deg, #11998e, #38ef7d)",
    importando: false,
    progresso: 0,
    concluido: false,
    arquivoNome: "",
    file: null,
    isDragging: false,
  },
]);

const exportando = ref(false);
const mostrarAviso = ref(false);
const avisoSucesso = ref(false);
const textoAviso = ref("");

function exibirAviso(texto, sucesso = false) {
  textoAviso.value = texto;
  avisoSucesso.value = sucesso;
  mostrarAviso.value = true;
}

function fecharAviso() {
  mostrarAviso.value = false;
}

const totalObrigatorios = computed(() => {
  return relatorios.filter((r) => r.obrigatorio).length;
});

const totalObrigatoriosConcluidos = computed(() => {
  return relatorios.filter((r) => r.obrigatorio && r.concluido && r.file)
    .length;
});

const relatorioOrcamentoConcluido = computed(() => {
  const orcamento = relatorios.find((r) => r.id === "orcamento");
  return Boolean(orcamento && orcamento.concluido && orcamento.file);
});

const todosObrigatoriosConcluidos = computed(() => {
  const obrigatorios = relatorios.filter((r) => r.obrigatorio);
  const obrigatoriosOk =
    obrigatorios.length > 0 && obrigatorios.every((r) => r.concluido && r.file);
  const nenhumImportando = !relatorios.some((r) => r.importando);

  return obrigatoriosOk && nenhumImportando;
});

function triggerImport(id) {
  const fileInput = document.getElementById(`fileInput-${id}`);
  if (fileInput) {
    fileInput.value = "";
    fileInput.click();
  }
}

function onFileSelected(event, relatorio) {
  const file = event.target.files[0];
  if (!file) return;

  if (!file.name.toLowerCase().endsWith(".csv")) {
    event.target.value = "";
    exibirAviso("Por favor, selecione um arquivo no formato CSV.");
    return;
  }

  iniciarImportacao(relatorio, file);
}

function onDrop(event, relatorio) {
  relatorio.isDragging = false;

  const file = event.dataTransfer.files[0];
  if (!file) return;

  if (!file.name.toLowerCase().endsWith(".csv")) {
    exibirAviso("Por favor, selecione um arquivo no formato CSV.");
    return;
  }

  iniciarImportacao(relatorio, file);
}

function removerArquivo(relatorio) {
  relatorio.concluido = false;
  relatorio.arquivoNome = "";
  relatorio.file = null;
  relatorio.progresso = 0;
  relatorio.isDragging = false;
  const fileInput = document.getElementById(`fileInput-${relatorio.id}`);
  if (fileInput) {
    fileInput.value = "";
  }
}

async function iniciarImportacao(relatorio, file) {
  relatorio.importando = true;
  relatorio.progresso = 15;
  relatorio.concluido = false;
  relatorio.arquivoNome = file.name;
  relatorio.file = file;

  const interval = setInterval(() => {
    if (relatorio.progresso < 90) {
      relatorio.progresso += Math.random() * 20 + 5;
    }
  }, 40);

  setTimeout(() => {
    clearInterval(interval);
    relatorio.progresso = 100;
    setTimeout(() => {
      relatorio.importando = false;
      relatorio.concluido = true;
    }, 150);
  }, 300);
}

async function exportarParaGex() {
  exportando.value = true;

  try {
    const formData = new FormData();

    const mapCampos = {
      atividade: "fileAtividades",
      autorizacao: "fileAutorizacoes",
      membro: "fileMembros",
      curricularizacao: "fileCurricularizadas",
      parceria: "fileParcerias",
      local: "fileLocais",
      palavraChave: "filePalavrasChaves",
      indicador: "fileIndicadores",
      cronograma: "fileCronogramas",
      orcamento: "fileOrcamento",
    };

    let arquivosAnexados = 0;

    for (const relatorio of relatorios) {
      if (relatorio.file && mapCampos[relatorio.id]) {
        formData.append(
          mapCampos[relatorio.id],
          relatorio.file,
          relatorio.arquivoNome
        );

        arquivosAnexados++;
      }
    }

    if (arquivosAnexados === 0) {
      exibirAviso(
        "Nenhum arquivo CSV válido selecionado para exportação."
      );
      return;
    }

    const res = await sigaaService.importarLote(formData);

    // Limpa os estados após o sucesso da requisição.
    relatorios.forEach((relatorio) => {
      relatorio.concluido = false;
      relatorio.arquivoNome = "";
      relatorio.file = null;
      relatorio.progresso = 0;
      relatorio.isDragging = false;

      const fileInput = document.getElementById(
        `fileInput-${relatorio.id}`
      );

      if (fileInput) {
        fileInput.value = "";
      }
    });

    exibirAviso(
      res ||
      "Importação iniciada com sucesso! Os dados estão sendo processados em background.",
      true
    );
  } catch (error) {
    console.error("Erro na finalização do SIGAA:", error);

    exibirAviso(
      `Erro ao exportar dados para o GEX: ${error.data?.message ||
      error.message ||
      "Erro de conexão."
      }`
    );
  } finally {
    exportando.value = false;
  }
}
</script>

<style scoped>
.importacao-container {
  padding: 8px;
  background: transparent;
  font-family: var(--font-base);
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ========== HEADER ========== */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  background: hsla(0, 0%, 100%, 0.03);
  border: 1px solid hsla(0, 0%, 100%, 0.08);
  border-radius: 8px;
  color: var(--branco);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: hsla(0, 0%, 100%, 0.08);
  border-radius: 8px;
  flex-shrink: 0;
}

.header-text h1 {
  font-size: 1.35rem;
  font-weight: 600;
  margin: 0 0 2px;
}

.header-text p {
  font-size: 0.85rem;
  margin: 0;
  opacity: 0.7;
}

.header-pills {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.status-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: hsla(258, 70%, 58%, 0.15);
  border: 1px solid hsla(258, 70%, 58%, 0.35);
  border-radius: 20px;
  font-size: 0.8rem;
  color: #e0e7ff;
}

.status-pill.pill-complete {
  background: hsla(135, 45%, 35%, 0.2);
  border-color: hsla(135, 45%, 35%, 0.45);
  color: var(--verde-claro, #4ade80);
}

.status-pill.pill-secondary {
  background: hsla(0, 0%, 100%, 0.05);
  border-color: hsla(0, 0%, 100%, 0.12);
  color: hsla(0, 0%, 100%, 0.75);
}

.pill-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--roxo-claro, #a78bfa);
}

.pill-complete .pill-dot {
  background: var(--verde, #22c55e);
}

.pill-dot.opcional {
  background: #facc15;
}

/* ========== LISTA DE RELATÓRIOS ========== */
.relatorios-lista {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ========== CARD RETANGULAR ========== */
.relatorio-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--roxo-escuro);
  border: 1px solid hsla(0, 0%, 100%, 0.08);
  border-radius: 8px;
  padding: 16px 20px;
  transition: all 0.25s ease;
  gap: 16px;
}

.relatorio-card:hover {
  background: var(--roxo-primario);
  border-color: hsla(0, 0%, 100%, 0.15);
  transform: translateX(4px);
}

.relatorio-card.card-importing {
  border-color: hsla(258, 70%, 58%, 0.5);
  background: var(--roxo-escuro);
}

.relatorio-card.card-done {
  border-color: hsla(135, 45%, 35%, 0.45);
}

.relatorio-card.card-dragover {
  border-color: var(--roxo-claro);
  background: var(--roxo-primario);
  box-shadow: 0 0 12px hsla(258, 70%, 58%, 0.35);
  transform: scale(1.005);
}

.relatorio-card.card-optional {
  border-style: dashed;
}

/* Card Info (Esquerda) */
.card-info {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
}

.card-icon {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--branco);
  flex-shrink: 0;
}

.card-icon :deep(svg) {
  width: 20px;
  height: 20px;
}

.card-details {
  min-width: 0;
  flex: 1;
}

.card-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
  flex-wrap: wrap;
}

.card-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--branco);
  margin: 0;
}

.badge-tipo {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
}

.badge-obrigatorio {
  background: hsla(258, 70%, 58%, 0.2);
  color: #c4b5fd;
  border: 1px solid hsla(258, 70%, 58%, 0.35);
}

.badge-opcional {
  background: hsla(45, 95%, 50%, 0.15);
  color: #fde047;
  border: 1px solid hsla(45, 95%, 50%, 0.35);
}

.card-desc {
  font-size: 0.78rem;
  color: hsla(0, 0%, 100%, 0.6);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.import-success-info {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 0.75rem;
  color: var(--verde, #22c55e);
  font-weight: 500;
}

/* Card Actions (Direita) */
.card-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.actions-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Progress bar inline */
.progress-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  width: 140px;
}

.progress-bar-container {
  width: 100%;
  height: 6px;
  background: hsla(0, 0%, 100%, 0.1);
  border-radius: 3px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--roxo-claro), var(--roxo-claro));
  border-radius: 3px;
  transition: width 0.2s ease;
}

.progress-text {
  font-size: 0.7rem;
  color: hsla(0, 0%, 100%, 0.7);
}

/* Botão Importar */
.btn-importar-acao {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--roxo-claro);
  color: var(--branco);
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-importar-acao:hover {
  background: var(--roxo-medio);
  transform: translateY(-1px);
}

.btn-importar-acao:active {
  transform: translateY(0);
}

.btn-importar-acao.btn-concluido {
  background: hsla(135, 45%, 35%, 0.2);
  color: var(--verde);
  border: 1px solid hsla(135, 45%, 35%, 0.4);
}

.btn-importar-acao.btn-concluido:hover {
  background: hsla(135, 45%, 35%, 0.3);
  transform: translateY(-1px);
}

.btn-remover-arquivo {
  background: hsla(0, 0%, 100%, 0.08);
  border: 1px solid hsla(0, 0%, 100%, 0.12);
  color: hsla(0, 0%, 100%, 0.65);
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-remover-arquivo:hover {
  background: hsla(0, 75%, 55%, 0.2);
  border-color: hsla(0, 75%, 55%, 0.4);
  color: #ff6b6b;
  transform: translateY(-1px);
}

/* ========== FINALIZAÇÃO IMPORTAÇÃO ========== */
.finalizacao-section {
  margin-top: 10px;
  padding: 20px;
  background: hsla(135, 45%, 35%, 0.08);
  border: 1px solid hsla(135, 45%, 35%, 0.25);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  animation: fadeIn 0.4s ease-in-out;
}

.finalizacao-alert {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--verde-claro, #4ade80);
  font-size: 0.88rem;
  text-align: left;
  line-height: 1.4;
}

.finalizacao-texto {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.finalizacao-texto strong {
  font-size: 0.92rem;
  color: var(--verde, #22c55e);
}

.btn-exportar-gex {
  background: var(--verde);
  color: var(--branco);
  border: none;
  border-radius: 6px;
  padding: 12px 36px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 14px hsla(135, 45%, 35%, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
}

.btn-exportar-gex:hover:not(:disabled) {
  background: var(--verde);
  transform: translateY(-1px);
  box-shadow: 0 6px 20px hsla(135, 45%, 35%, 0.4);
}

.btn-exportar-gex:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.spinner-mini {
  width: 16px;
  height: 16px;
  border: 2px solid hsla(0, 0%, 100%, 0.3);
  border-top-color: var(--branco);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes fadeIn {
  from {
    transform: translateY(10px);
  }

  to {
    transform: translateY(0);
  }
}

/* ========== RESPONSIVO ========== */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-pills {
    width: 100%;
    flex-wrap: wrap;
  }

  .relatorio-card {
    flex-direction: column;
    align-items: stretch;
  }

  .card-actions {
    justify-content: flex-end;
    margin-top: 10px;
  }

  .progress-wrapper {
    width: 100%;
  }
}
</style>
